import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormFieldAny } from '@/components/FormField';
import { UserPickerDialog } from '@/features/users/components/UserPickerDialog';
import type { UserListItem } from '@/features/users/types';
import type { LinkedUser } from '../../types';

interface UserPickerFieldProps {
    linkedUser: LinkedUser | null;
    onSelect: (linkedUser: LinkedUser) => void;
    onClear: () => void;
    error?: string;
    // A user handed in from outside cannot be changed inside the form
    isLocked?: boolean;
}

/**
 * Replaces the invite email field: the admin searches an existing user account
 * and links it to the new employee contact.
 */
export function UserPickerField({
    linkedUser,
    onSelect,
    onClear,
    error,
    isLocked,
}: UserPickerFieldProps) {
    const { t } = useTranslation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handleSelect = (user: UserListItem) => {
        onSelect({ userId: user.id, username: user.username, email: user.email });
    };

    return (
        <FormFieldAny
            label=""
            labelKey="employees.createForm.linkedUser"
            error={error}
            mandatory
            noValue={!linkedUser}
        >
            {linkedUser ? (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-field px-3 py-2">
                    <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-onsurface">
                            {linkedUser.username}
                        </p>
                        <p className="truncate text-xs text-onsurface/60">{linkedUser.email}</p>
                    </div>

                    {!isLocked && (
                        <div className="flex shrink-0 items-center gap-3">
                            <button
                                type="button"
                                onClick={() => setIsDialogOpen(true)}
                                className="text-sm text-primary transition-colors hover:text-primary/80"
                            >
                                {t('employees.createForm.changeUser')}
                            </button>
                            <button
                                type="button"
                                onClick={onClear}
                                className="text-sm text-onsurface/60 transition-colors hover:text-onsurface"
                            >
                                {t('common.remove')}
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setIsDialogOpen(true)}
                    className="w-full rounded-lg border border-dashed border-border bg-field px-3 py-2 text-left text-sm text-onsurface/60 transition-colors hover:border-primary hover:text-onsurface"
                >
                    {t('employees.createForm.searchUser')}
                </button>
            )}

            <UserPickerDialog
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                onSelect={handleSelect}
                selectedUserId={linkedUser?.userId}
            />
        </FormFieldAny>
    );
}
